import { Alert, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, } from '@cargomatic/ui';
import AddButton from 'components/v2/buttons/AddButton';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import ApproveButton from 'components/v2/buttons/ApproveButton';
import DeclineButton from 'components/v2/buttons/DeclineButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import Dropdown from 'components/v2/dropdowns/DropdownV2';
import CargomaticInput from 'components/v2/input';
import CargomaticSelect from 'components/v2/select';
import ToolTip from 'components/v2/Tooltip';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from 'stores/ToastStore';
import { unescapeHTML } from 'underscore.string';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
import _ from 'lodash';
import classNames from 'classnames';
const HAZMAT_CLASSES = ['N/A', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
const HAZMAT_PACKAGING_GROUP = ['N/A', 'I', 'II', 'III'];
const hazmatClassesList = _.map(HAZMAT_CLASSES, (cl) => {
    return {
        value: cl,
        localizationSlug: cl === 'N/A' ? 'N_A' : cl.toString(),
    };
});
const hazmatPackagingGroupList = _.map(HAZMAT_PACKAGING_GROUP, (pg) => {
    return {
        value: pg,
        localizationSlug: pg === 'N/A' ? 'N_A' : pg.toString(),
    };
});
const isEmptyHazmat = (hazmatDetails) => {
    const { hazmat_class, hazmat_packaging_group, hazmat_description = '', } = hazmatDetails;
    return (hazmat_class === 'N/A' &&
        hazmat_packaging_group === 'N/A' &&
        hazmat_description.trim() === '');
};
const prepareOrderHazmatDetails = (order) => {
    var _a;
    if (featureEnabled(CONFIGURED_FEATURES.ENABLE_MULTIPLE_HAZMAT_DETAILS) &&
        ((_a = order === null || order === void 0 ? void 0 : order.hazmat_details) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        return order.hazmat_details;
    }
    const hazmatDetails = _.pick(order, [
        'hazmat_class',
        'hazmat_packaging_group',
        'hazmat_description',
    ]);
    if (isEmptyHazmat(hazmatDetails))
        return [];
    return [hazmatDetails];
};
const EditDropdown = ({ hazmatDetail, disabled = false, onSelect }) => {
    const { t } = useTranslation();
    const hazmatId = hazmatDetail._id;
    return (React.createElement(Dropdown, { useIcon: true, className: "", testDataId: `${hazmatId}-Dropdown-d41900dd`, disabled: disabled },
        React.createElement(Dropdown.Button, { dataTestId: `${hazmatId}-Dropdown-button-d41909dd`, onClick: () => onSelect(hazmatDetail, 'edit') }, t('EDIT')),
        React.createElement(Dropdown.Button, { dataTestId: `${hazmatId}-Dropdown-button-d46900dd`, onClick: () => onSelect(hazmatDetail, 'delete') }, t('DELETE'))));
};
const HazmatActionConfirmation = ({ hazmatDetail, action, disableApprove, handleApprove, handleCancel, }) => {
    const { t } = useTranslation();
    const hazmatId = hazmatDetail._id;
    const isEdit = action === 'edit';
    const approveTooltipLabel = isEdit
        ? t('SAVE_HAZMAT_DETAIL')
        : t('DELETE_HAZMAT_DETAIL');
    return (React.createElement("div", { className: "flex gap-[5px]" },
        React.createElement(ToolTip, { dataTestId: `${hazmatId}-CargomaticApproveTooltip-74bc9003`, id: `${hazmatId}_approve_button`, position: "left", bodyParams: {
                dataTestId: `${hazmatId}-ApproveButton-c6f695g8`,
                onClick: () => {
                    handleApprove(hazmatId);
                },
                disabled: disableApprove,
            }, body: ({ dataTestId, onClick, disabled }) => isEdit ? (React.createElement(ApproveButton, { dataTestId: dataTestId, onClick: onClick, disabled: disabled })) : (React.createElement(DeleteButton, { dataTestId: dataTestId, onClick: onClick, disabled: disabled })) }, approveTooltipLabel),
        React.createElement(ToolTip, { dataTestId: `${hazmatId}-CargomaticDeclineTooltip-09f91576`, id: `${hazmatId}_decline_button`, position: "left", bodyParams: {
                dataTestId: `${hazmatId}-DeclineButton-09f91576`,
                onClick: () => {
                    handleCancel(hazmatId);
                },
            }, body: ({ dataTestId, onClick }) => (React.createElement(DeclineButton, { onClick: onClick, dataTestId: dataTestId })) }, t('CANCEL'))));
};
const HazmatActionButton = ({ hazmatDetail, hasEditAccess, onDropdownSelect, }) => {
    const { t } = useTranslation();
    const hazmatId = hazmatDetail._id;
    if (!hasEditAccess) {
        return (React.createElement(ToolTip, { dataTestId: `${hazmatId}CargomaticTooltip-74bc9003`, id: `${hazmatId}document_list`, position: "left", bodyParams: {
                disabled: !hasEditAccess,
                onSelect: onDropdownSelect,
                hazmatDetail,
            }, body: EditDropdown }, t('HAZMAT_DETAIL_INFO_TOOLTIP')));
    }
    return (React.createElement(EditDropdown, { hazmatDetail: hazmatDetail, onSelect: onDropdownSelect }));
};
const CreateRow = ({ orderHazmatDetails, handleAddHazmatDetail, addDuplicateIndice, removeDuplicateIndice, duplicateIndices, setIsEmptyDuplication, isEmptyDuplication, }) => {
    const { t } = useTranslation();
    const defaultNAValue = { value: 'N/A', localizationSlug: 'N_A' };
    const [hazmatClass, setHazmatClass] = useState(defaultNAValue);
    const [hazmatPackagingGroup, setHazmatPackagingGroup] = useState(defaultNAValue);
    const [hazmatDescription, setHazmatDescription] = useState('');
    const isDuplicateCombination = () => {
        return orderHazmatDetails.some((obj) => {
            const { hazmat_class, hazmat_packaging_group, hazmat_description } = obj;
            return (hazmatClass.value === hazmat_class &&
                hazmatPackagingGroup.value === hazmat_packaging_group &&
                hazmatDescription === hazmat_description);
        });
    };
    useEffect(() => {
        const isDuplicate = isDuplicateCombination();
        if (isDuplicate) {
            addDuplicateIndice(-1);
            if (isEmptyHazmat({
                hazmat_class: hazmatClass.value,
                hazmat_packaging_group: hazmatPackagingGroup.value,
                hazmat_description: hazmatDescription,
            }))
                setIsEmptyDuplication(true);
        }
        else {
            removeDuplicateIndice(-1);
            setIsEmptyDuplication(false);
        }
    }, [
        hazmatClass,
        hazmatDescription,
        hazmatPackagingGroup,
        orderHazmatDetails,
    ]);
    const isEmptyData = () => {
        return isEmptyHazmat({
            hazmat_class: hazmatClass.value,
            hazmat_packaging_group: hazmatPackagingGroup.value,
            hazmat_description: hazmatDescription,
        });
    };
    const handleClassSelect = (item) => {
        setHazmatClass(item);
    };
    const handlePackagingGroupSelect = (item) => {
        setHazmatPackagingGroup(item);
    };
    return (React.createElement(TableRow, { className: classNames({
            'bg-red-50 border-red-200': duplicateIndices.includes(-1) && !isEmptyDuplication,
        }) },
        React.createElement(TableCell, null,
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-92dc5d28", showCaption: true, hideLabel: true, items: hazmatClassesList, selected: hazmatClass === null || hazmatClass === void 0 ? void 0 : hazmatClass.value, className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: handleClassSelect, applyDefaultMargins: false, valueKey: "value", textKey: "localizationSlug", caption: t('SELECT_HAZMAT_CLASS') })),
        React.createElement(TableCell, null,
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-6c1cb9a8", showCaption: true, hideLabel: true, items: hazmatPackagingGroupList, selected: (hazmatPackagingGroup === null || hazmatPackagingGroup === void 0 ? void 0 : hazmatPackagingGroup.value) || '', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: handlePackagingGroupSelect, applyDefaultMargins: false, valueKey: "value", textKey: "localizationSlug", caption: t('SELECT_HAZMAT_PACKAGING_GROUP') })),
        React.createElement(TableCell, null,
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-62f5540f", hideLabel: true, type: "text", name: "template-name", className: "w-full", placeholder: t('HAZMAT_DESCRIPTION'), value: hazmatDescription, onChange: (value) => {
                    setHazmatDescription(value);
                } })),
        React.createElement(TableCell, null,
            React.createElement(AddButton, { dataTestId: "AddButton-0ed24a4d", disabled: duplicateIndices.includes(-1) || isEmptyData(), onClick: () => {
                    handleAddHazmatDetail({
                        hazmatClass,
                        hazmatPackagingGroup,
                        hazmatDescription,
                    });
                    setHazmatClass(defaultNAValue);
                    setHazmatPackagingGroup(defaultNAValue);
                    setHazmatDescription('');
                } }))));
};
const EditRow = ({ orderHazmatDetails, addDuplicateIndice, removeDuplicateIndice, duplicateIndices, rowIndex, hazmatDetail, handleApproveEdit, handleCancelEdit, }) => {
    const { t } = useTranslation();
    const [hazmatDetailData, setHazmatDetailData] = useState(hazmatDetail);
    const hazmatId = hazmatDetail._id;
    const handleCancel = (hazmatId) => {
        removeDuplicateIndice(rowIndex);
        handleCancelEdit(hazmatId);
    };
    const emptyHazmat = isEmptyHazmat(hazmatDetailData);
    const disableApprove = emptyHazmat || duplicateIndices.includes(rowIndex);
    const buttonTooltipSlug = emptyHazmat
        ? 'CANNOT_SAVE_DATA_WITH_EMPTY_HAZMAT'
        : 'SAVE_HAZMAT_DETAIL';
    const handleEditHazmatDetail = (field, value) => {
        let newHazmatDetail = {
            [field]: value,
        };
        setHazmatDetailData((prev) => {
            return Object.assign(Object.assign({}, prev), newHazmatDetail);
        });
    };
    const isDuplicateRow = () => {
        const { hazmat_class: hazmatClass, hazmat_packaging_group: hazmatPackagingGroup, hazmat_description: hazmatDescription, } = hazmatDetailData;
        return orderHazmatDetails.some((obj, index) => {
            if (index === rowIndex)
                return false;
            const { hazmat_class, hazmat_packaging_group, hazmat_description } = obj;
            return (hazmatClass === hazmat_class &&
                hazmatPackagingGroup === hazmat_packaging_group &&
                hazmatDescription === hazmat_description);
        });
    };
    useEffect(() => {
        const isDuplicate = isDuplicateRow();
        if (isDuplicate)
            addDuplicateIndice(rowIndex);
        else
            removeDuplicateIndice(rowIndex);
    }, [hazmatDetailData, orderHazmatDetails]);
    return (React.createElement(TableRow, { key: hazmatId, className: classNames({
            'bg-red-50 border-red-200': duplicateIndices.includes(rowIndex),
        }) },
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticSelect, { dataTestId: `CargomaticSelect-92dc5d28-${hazmatId}`, showCaption: true, hideLabel: true, items: hazmatClassesList, selected: hazmatDetailData.hazmat_class || 'N/A', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: (selectedItem) => {
                    handleEditHazmatDetail('hazmat_class', selectedItem.value);
                }, applyDefaultMargins: false, valueKey: "value", textKey: "localizationSlug", caption: t('SELECT_HAZMAT_CLASS') })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticSelect, { dataTestId: "CargomaticSelect-6c1cb9a8", showCaption: true, hideLabel: true, items: hazmatPackagingGroupList, selected: hazmatDetailData.hazmat_packaging_group || 'N/A', className: "w-full", inputClassNames: "max-w-full min-w-full", onSelect: (selectedItem) => {
                    handleEditHazmatDetail('hazmat_packaging_group', selectedItem.value);
                }, applyDefaultMargins: false, valueKey: "value", textKey: "localizationSlug", caption: t('SELECT_HAZMAT_PACKAGING_GROUP') })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(CargomaticInput, { dataTestId: "CargomaticInput-62f5540f", hideLabel: true, type: "text", name: "template-name", className: "w-full", placeholder: t('HAZMAT_DESCRIPTION'), value: hazmatDetailData.hazmat_description, onChange: (value) => {
                    handleEditHazmatDetail('hazmat_description', value);
                } })),
        React.createElement(TableCell, { className: "break-words" },
            React.createElement(HazmatActionConfirmation, { hazmatDetail: hazmatDetailData, action: "edit", handleCancel: handleCancel, handleApprove: () => handleApproveEdit(hazmatDetailData), disableApprove: disableApprove, key: `${hazmatId}-hazmat-action`, buttonTooltip: t(buttonTooltipSlug) }))));
};
const ReadOnlyRow = ({ action, hazmatDetail, hasEditAccess, handleApproveDelete, handleCancelDelete, onDropdownSelect, }) => {
    const { t } = useTranslation();
    return (React.createElement(TableRow, { key: hazmatDetail._id },
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(hazmatDetail.hazmat_class)),
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(hazmatDetail.hazmat_packaging_group)),
        React.createElement(TableCell, { className: "break-words" }, unescapeHTML(hazmatDetail.hazmat_description)),
        hasEditAccess && (React.createElement(TableCell, { className: "break-words" }, action === 'delete' ? (React.createElement(HazmatActionConfirmation, { hazmatDetail: hazmatDetail, action: "delete", key: `${hazmatDetail._id}-hazmat-action`, handleApprove: handleApproveDelete, handleCancel: handleCancelDelete, buttonTooltip: t('DELETE_HAZMAT_DETAIL') })) : (React.createElement(HazmatActionButton, { hazmatDetail: hazmatDetail, hasEditAccess: hasEditAccess, onDropdownSelect: onDropdownSelect }))))));
};
const HazmatRow = ({ orderHazmatDetails, addDuplicateIndice, removeDuplicateIndice, duplicateIndices, rowIndex, handleApproveDelete, handleUpdateHazmat, handleCancelEdit, handleCancelDelete, onDropdownSelect, hazmatDetail, hasEditAccess, editingIds, }) => {
    const hazmatId = hazmatDetail._id;
    const action = editingIds[hazmatId];
    switch (action) {
        case 'edit':
            return (React.createElement(EditRow, { orderHazmatDetails: orderHazmatDetails, addDuplicateIndice: addDuplicateIndice, removeDuplicateIndice: removeDuplicateIndice, duplicateIndices: duplicateIndices, handleApproveEdit: handleUpdateHazmat, handleCancelEdit: handleCancelEdit, hazmatDetail: hazmatDetail, rowIndex: rowIndex, key: `edit_row_${hazmatId}` }));
        case 'delete':
            return (React.createElement(ReadOnlyRow, { handleApproveDelete: handleApproveDelete, handleCancelDelete: handleCancelDelete, action: action, hasEditAccess: hasEditAccess, hazmatDetail: hazmatDetail, onDropdownSelect: onDropdownSelect, key: `readonly_row_delete` }));
        default:
            return (React.createElement(ReadOnlyRow, { action: action, hasEditAccess: hasEditAccess, onDropdownSelect: onDropdownSelect, hazmatDetail: hazmatDetail, key: `readonly_row_read` }));
    }
};
function HazmatDetails({ order, updateOrderHazmatDetails, isEditable }) {
    const { t } = useTranslation();
    const [duplicateIndices, setDuplicateIndices] = useState([]);
    const [isEmptyDuplication, setIsEmptyDuplication] = useState(false);
    const orderHazmatDetails = useMemo(() => prepareOrderHazmatDetails(order), [order]);
    // Map of hazmat_detail_id : 'action'
    const [editingIds, setEditingIds] = useState({});
    const hasEditAccess = useMemo(() => true) && isEditable;
    const handleClearForm = (hazmatDetailId) => {
        if (hazmatDetailId) {
            return setEditingIds((prev) => {
                const updated = Object.assign({}, prev);
                delete updated[hazmatDetailId];
                return updated;
            });
        }
        return setEditingIds({});
    };
    const handleAddHazmatDetail = async ({ hazmatClass, hazmatPackagingGroup, hazmatDescription = '', }) => {
        const hazmat_class = hazmatClass.value || 'N/A';
        const hazmat_packaging_group = hazmatPackagingGroup.value || 'N/A';
        const hazmatDescriptionTrimmed = (hazmatDescription === null || hazmatDescription === void 0 ? void 0 : hazmatDescription.trim()) || '';
        const updatedOrderHazmatDetails = _.concat(orderHazmatDetails, {
            hazmat_class,
            hazmat_packaging_group,
            hazmat_description: hazmatDescriptionTrimmed,
        });
        try {
            await updateOrderHazmatDetails(order._id, updatedOrderHazmatDetails);
            // handleClearForm();
        }
        catch (_error) {
            showToast(t('HAZMAT_DETAIL_CREATION_FAIL'), '', 'error');
        }
    };
    const handleUpdateHazmatDetails = async (hazmatDetailData) => {
        const hazmatDetailsIndex = orderHazmatDetails.findIndex((hd) => hd._id.toString() === hazmatDetailData._id);
        let updatedOrderHazmatDetails = _.clone(orderHazmatDetails);
        updatedOrderHazmatDetails.splice(hazmatDetailsIndex, 1, hazmatDetailData);
        try {
            await updateOrderHazmatDetails(order._id, updatedOrderHazmatDetails);
            handleClearForm(hazmatDetailData._id);
        }
        catch (_error) {
            showToast(t('HAZMAT_DETAIL_UPDATION_FAIL'), '', 'error');
        }
    };
    const handleDeleteHazmatDetails = async (hazmat_id) => {
        const updatedOrderHazmatDetails = _.clone(orderHazmatDetails).filter((hd) => hd._id.toString() !== hazmat_id);
        try {
            await updateOrderHazmatDetails(order._id, updatedOrderHazmatDetails);
            handleClearForm(hazmat_id);
        }
        catch (_error) {
            showToast(t('HAZMAT_DETAIL_DELETION_FAIL'), '', 'error');
        }
    };
    const handleCancelEdit = (hazmat_id) => {
        setEditingIds((prev) => {
            const updated = Object.assign({}, prev);
            delete updated[hazmat_id];
            return updated;
        });
    };
    const handleCancelDelete = (hazmat_id) => {
        setEditingIds((prev) => {
            const updated = Object.assign({}, prev);
            delete updated[hazmat_id];
            return updated;
        });
    };
    const handleApproveDelete = (hazmat_id) => {
        handleDeleteHazmatDetails(hazmat_id);
    };
    const onDropdownSelect = (hazmat, action) => {
        setEditingIds((prev) => (Object.assign(Object.assign({}, prev), { [hazmat._id]: action })));
    };
    const addDuplicateIndice = (index) => {
        setDuplicateIndices((prev) => {
            return [...prev, index];
        });
    };
    const removeDuplicateIndice = (index) => {
        setDuplicateIndices((prev) => {
            return prev.filter((i) => i !== index);
        });
    };
    const renderDuplicateAlert = () => {
        return (React.createElement(Alert, { type: "error", className: "alert-error bg-red-100 text-red-900 font-medium", "data-testid": "alert" }, t('DUPLICATE_COMBINATIONS_FOUND')));
    };
    return (React.createElement(CargomaticCollapsibleCard, { id: "order-hazmat-details", header: () => t('HAZARDOUS_MATERIALS') },
        React.createElement("div", { className: "overflow-x-auto" },
            Boolean(duplicateIndices.length) &&
                !isEmptyDuplication &&
                renderDuplicateAlert(),
            !(orderHazmatDetails === null || orderHazmatDetails === void 0 ? void 0 : orderHazmatDetails.length) && !isEditable ? (React.createElement("p", { className: "py-4 sm:py-4" }, t('NO_HAZMAT_DETAILS_ON_ORDER'))) : (React.createElement(Table, null,
                React.createElement(TableHeader, null,
                    React.createElement(TableRow, null,
                        React.createElement(TableHead, { className: "whitespace-nowrap", "data-testid": "TableHead-f4ff494b" }, t('HAZMAT_CLASS')),
                        React.createElement(TableHead, { className: "whitespace-nowrap", "data-testid": "TableHead-f4ff494t" }, t('HAZMAT_PACKAGING_GROUP')),
                        React.createElement(TableHead, { className: "whitespace-nowrap", "data-testid": "TableHead-f4ff494r" }, t('HAZMAT_DESCRIPTION')),
                        hasEditAccess && (React.createElement(TableHead, { className: "whitespace-nowrap", "data-testid": "TableHead-f4ff494i" }, t('ACTION'))))),
                React.createElement(TableBody, null,
                    hasEditAccess && (React.createElement(CreateRow, { addDuplicateIndice: addDuplicateIndice, removeDuplicateIndice: removeDuplicateIndice, duplicateIndices: duplicateIndices, orderHazmatDetails: orderHazmatDetails, handleAddHazmatDetail: handleAddHazmatDetail, setIsEmptyDuplication: setIsEmptyDuplication, isEmptyDuplication: isEmptyDuplication, key: "create-row" })), orderHazmatDetails === null || orderHazmatDetails === void 0 ? void 0 :
                    orderHazmatDetails.map((hazmatDetail, index) => (React.createElement(HazmatRow, { orderHazmatDetails: orderHazmatDetails, addDuplicateIndice: addDuplicateIndice, removeDuplicateIndice: removeDuplicateIndice, duplicateIndices: duplicateIndices, rowIndex: index, handleApproveDelete: handleApproveDelete, handleUpdateHazmat: handleUpdateHazmatDetails, handleCancelEdit: handleCancelEdit, handleCancelDelete: handleCancelDelete, onDropdownSelect: onDropdownSelect, hazmatDetail: hazmatDetail, hasEditAccess: hasEditAccess, editingIds: editingIds, key: `hazmat_row_${index}` })))))))));
}
HazmatDetails.propTypes = {
    order: PropTypes.object.isRequired,
    updateOrderHazmatDetails: PropTypes.func,
    isEditable: PropTypes.bool.isRequired,
};
HazmatDetails.defaultProps = { isEditable: false };
export default HazmatDetails;
